// Geerbte Anschrift und geerbte Kontaktwege (P7 A, Michael E2; Vorgabe DESIGN
// geerbt-link-etikett mit V3a′). Der Abruf, die Herkunft und der Feldname.

import { useEffect, useState, type ReactElement } from 'react';
import { Caption1 } from '@fluentui/react-components';

import { partyHref } from '../../parties.ts';
import { AppLink } from '../../shell/router.tsx';
import { Symbol } from '../../shell/symbole.tsx';
import { fetchViaOrganization, weitereOrganisationen, type ViaOrganization } from '../../via-organization.ts';
import { useKontaktStyles } from './stile.ts';

/**
 * Was die Person von ihrer Organisation übernimmt; `undefined`, solange nichts
 * geladen ist, bei jedem Fehler und an einer Organisation selbst. Ein Fehler
 * zeigt nichts an: die eigenen Abschnitte laden unabhängig davon.
 */
export function useUeberOrganisation(options: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly partyId: string;
  readonly aktiv: boolean;
  /** Ändert sich der Stand des Kontakts, wird neu gelesen. */
  readonly stand: unknown;
}): ViaOrganization | undefined {
  const { apiBaseUrl, accessToken, partyId, aktiv, stand } = options;
  const [ueber, setUeber] = useState<ViaOrganization | undefined>(undefined);
  useEffect(() => {
    let gueltig = true;
    setUeber(undefined);
    if (!aktiv) return undefined;
    void (async () => {
      try {
        const token = await accessToken();
        if (!gueltig || token === undefined) return;
        const geladen = await fetchViaOrganization({ baseUrl: apiBaseUrl, accessToken: token, partyId });
        if (gueltig) setUeber(geladen);
      } catch {
        // Nichts übernommen; die eigenen Daten stehen unberührt.
      }
    })();
    return () => {
      gueltig = false;
    };
  }, [apiBaseUrl, accessToken, partyId, aktiv, stand]);
  return ueber;
}

/**
 * Die Herkunft unter einem geerbten Wert: "<Verwendung> · <Organisation>[ und N
 * weitere Organisationen]" (V3a′). Der Mittelpunkt wird als "von" vorgelesen;
 * ein geschütztes Leerzeichen hält ihn beim Namen. Weitere Organisationen
 * stehen ausgeschrieben da, sichtbar wie vorgelesen (eine Caption ist keine
 * Spalte, keine Kurzform).
 */
export function HerkunftCaption({ ueber, verwendung }: { readonly ueber: ViaOrganization; readonly verwendung?: string }): ReactElement {
  const own = useKontaktStyles();
  const weitere = weitereOrganisationen(ueber.additionalEmploymentCount);
  // Das Modell liefert die Verwendung klein ("geschäftlich"); am Zeilenanfang steht sie groß.
  const wort = verwendung === undefined || verwendung === '' ? undefined : `${verwendung.charAt(0).toLocaleUpperCase('de-DE')}${verwendung.slice(1)}`;
  return (
    <Caption1 className={own.ueberOrganisation}>
      {wort === undefined ? undefined : (
        <>
          {wort}
          <span aria-hidden="true">{' ·\u00a0'}</span>
          <span className={own.nurVorgelesen}> von </span>
        </>
      )}
      <AppLink to={partyHref(ueber.organizationPartyId)}>{ueber.organizationName}</AppLink>
      {weitere === undefined ? undefined : ` ${weitere}`}
    </Caption1>
  );
}

/**
 * Der Feldname einer geerbten Angabe mit Verknüpfungszeichen; vorgelesen
 * "<Feldname>, geerbt" (Vorgabe DESIGN 16.10., Nachtrag 19.10. V1′). Der Wortverbinder (U+2060)
 * verbietet den Umbruch vor dem Zeichen, es steht nie allein in einer Zeile.
 * Das Zeichen 4 px über der Schrift des Feldnamens (16 px): kleiner las es sich
 * nicht als Kettenglied; mit der Grundschrift wächst nur dieser Zuschlag, sonst
 * stünde es bei 200 % fast dreimal so hoch wie das Wort.
 */
export function UebernommenerFeldname({ label }: { readonly label: string }): ReactElement {
  const own = useKontaktStyles();
  return (
    <>
      <span className={own.uebernommenZeichen}>
        {label}
        <span aria-hidden="true">{'\u2060'}</span>
        <Symbol name="verknuepft" groesse="calc(1em + 0.25rem)" />
      </span>
      <span className={own.nurVorgelesen}>, geerbt</span>
    </>
  );
}
